import { useEffect, useRef } from 'react';
import { api } from 'api';

const CHATWOOT_BASE_URL = 'https://support.primemoq.app';
const CHATWOOT_WEBSITE_TOKEN = 'TDLebVTp5jgCjQssa3SNgbHW';

let chatwootLoadPromise = null;

function loadChatwootScript() {
  if (chatwootLoadPromise) return chatwootLoadPromise;

  chatwootLoadPromise = new Promise((resolve, reject) => {
    if (window.chatwootSDK) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = `${CHATWOOT_BASE_URL}/packs/js/sdk.js`;
    script.async = true;
    script.onload = () => {
      window.chatwootSDK.run({
        websiteToken: CHATWOOT_WEBSITE_TOKEN,
        baseUrl: CHATWOOT_BASE_URL,
      });
      resolve();
    };
    script.onerror = () => reject(new Error('Failed to load Chatwoot script'));
    document.head.appendChild(script);
  });

  return chatwootLoadPromise;
}

// identity is the payload from GET /api/chatwoot/identity (see
// Prime-MOQ-Backend's ChatwootService): { identifier, identifierHash,
// standard, customAttributes }. `standard` already only contains the
// Chatwoot-recognized contact fields (email, name, phone_number, ...) that
// have a real value, so it can be spread straight into setUser. Passing
// identifier_hash is what turns this into Identity Validation — it lets
// Chatwoot trust the identifier and show the merchant their past
// conversations instead of starting a fresh anonymous one.
function applyChatwootIdentity(identity) {
  if (!window.$chatwoot || !identity?.identifier) return;

  window.$chatwoot.setUser(identity.identifier, {
    ...identity.standard,
    identifier_hash: identity.identifierHash,
  });

  if (identity.customAttributes && Object.keys(identity.customAttributes).length) {
    window.$chatwoot.setCustomAttributes(identity.customAttributes);
  }
}

// Loads the Chatwoot widget, and — only once there's an authenticated shop
// session — fetches this shop's identity-validation payload from the
// backend and applies it to the widget. Nothing is fetched or identified
// while isLoggedIn is false (e.g. the support-login/impersonation path,
// which has no real Shopify session for the backend to authenticate).
export const useChatwoot = (isLoggedIn) => {
  const identityPromiseRef = useRef(null);

  useEffect(() => {
    loadChatwootScript().catch((error) => console.error(error));
  }, []);

  useEffect(() => {
    if (!isLoggedIn) return;

    let cancelled = false;
    let onReady;

    if (!identityPromiseRef.current) {
      identityPromiseRef.current = api.getChatwootIdentity().catch((error) => {
        console.error(error);
        identityPromiseRef.current = null;
        return null;
      });
    }

    identityPromiseRef.current.then((identity) => {
      if (cancelled || !identity) return;

      if (window.$chatwoot) {
        applyChatwootIdentity(identity);
      } else {
        onReady = () => applyChatwootIdentity(identity);
        window.addEventListener('chatwoot:ready', onReady, { once: true });
      }
    });

    return () => {
      cancelled = true;
      if (onReady) window.removeEventListener('chatwoot:ready', onReady);
    };
  }, [isLoggedIn]);
};
